import { useState } from "react";
import {
  Box, Stack, Typography, TextField, Button, Alert, Chip, Paper, CircularProgress,
  ToggleButton, ToggleButtonGroup, Collapse, Link as MuiLink,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import HistoryIcon from "@mui/icons-material/History";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import {
  buscarCuenta, BuscarCuentaResult, CuentaImputada,
  iniciarAuditoria, esperarAuditoria, AuditEvento, AuditoriaCuenta,
} from "../services/monitoreoVivo";

// CUIT 23389951889 → 23-38995188-9 (solo presentación).
function fmtCuit(c?: string | null): string | null {
  const d = String(c ?? "").replace(/\D/g, "");
  return d.length === 11 ? `${d.slice(0, 2)}-${d.slice(2, 10)}-${d.slice(10)}` : c || null;
}

// Los logs y la base guardan UTC; operaciones razona en hora Argentina (UTC-3,
// sin horario de verano desde 2009). Mostrar el ISO crudo es la fuente del
// malentendido clásico: "el Last Update no coincide con la hora del pago".
function fmtArt(iso?: string | null, conMs = false): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  const p = new Intl.DateTimeFormat("es-AR", {
    timeZone: "America/Argentina/Buenos_Aires",
    day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false,
  }).format(d);
  return conMs ? `${p}.${String(d.getUTCMilliseconds()).padStart(3, "0")}` : p;
}

/** "Last Update:2026-09-14T17:29:23.655Z" → el ISO, para anclar la auditoría. */
function lastUpdateIso(historia?: string | null): string | null {
  const m = /Last Update:\s*([0-9T:.\-+Z]+)/i.exec(String(historia ?? ""));
  return m ? m[1] : null;
}

const TIPO_ESTILO: Record<string, { label: string; fg: string; bg: string }> = {
  alta:      { label: "ALTA",      fg: "#0F5C6E", bg: "#DAEAEE" },
  alias:     { label: "ALIAS",     fg: "#1E40AF", bg: "#DBEAFE" },
  apertura:  { label: "APERTURA",  fg: "#166534", bg: "#DCFCE7" },
  cierre:    { label: "CIERRE",    fg: "#991B1B", bg: "#FEE2E2" },
  pago:      { label: "PAGO",      fg: "#166534", bg: "#DCFCE7" },
  cobro:     { label: "COBRO",     fg: "#3F6212", bg: "#ECFCCB" },
  coelsa:    { label: "COELSA",    fg: "#6B7280", bg: "#F1F5F9" },
  cambio:    { label: "CAMBIO",    fg: "#92400E", bg: "#FEF3C7" },
  llamado:   { label: "LLAMADO",   fg: "#6B7280", bg: "#F1F5F9" },
  respuesta: { label: "RESPUESTA", fg: "#6B7280", bg: "#F1F5F9" },
};

function KV({ data }: { data: Record<string, React.ReactNode> }) {
  return (
    <Box component="dl" sx={{ display: "grid", gridTemplateColumns: "170px 1fr", rowGap: 0.5, m: 0, fontSize: 13 }}>
      {Object.entries(data).filter(([, v]) => v !== null && v !== undefined && v !== "").map(([k, v]) => (
        <Box key={k} sx={{ display: "contents" }}>
          <Box component="dt" sx={{ color: "text.secondary" }}>{k}</Box>
          <Box component="dd" sx={{ m: 0, wordBreak: "break-word" }}>{v}</Box>
        </Box>
      ))}
    </Box>
  );
}

/** Quién hizo el llamado, en una línea legible. */
function actorTexto(e: AuditEvento): string | null {
  const partes: string[] = [];
  if (e.auth === "apikey") partes.push(`API key de ${e.collector ?? "?"}`);
  else if (e.auth === "jwt") partes.push(`usuario ${e.usuario ?? "?"}`);
  else if (e.usuario) partes.push(e.usuario);
  else if (e.auth === "sin token") partes.push("sin token (entrante)");
  if (e.ip) partes.push(`IP ${e.ip}`);
  if (e.user_agent) partes.push(e.user_agent);
  return partes.length ? partes.join(" · ") : null;
}

function EventoFila({ e }: { e: AuditEvento }) {
  const [abierto, setAbierto] = useState(false);
  const est = TIPO_ESTILO[e.tipo] ?? TIPO_ESTILO.llamado;
  const clave = e.tipo === "cierre" || e.tipo === "pago" || e.tipo === "apertura";
  const actor = actorTexto(e);
  return (
    <Box sx={{
      display: "grid", gridTemplateColumns: { xs: "1fr", sm: "150px 96px 1fr" },
      gap: 1, alignItems: "start", py: 0.9, px: 1,
      borderBottom: "1px solid", borderColor: "divider",
      bgcolor: clave ? est.bg : "transparent",
    }}>
      <Box sx={{ fontFamily: "monospace", fontSize: 12, color: "text.secondary", fontVariantNumeric: "tabular-nums" }}>
        {fmtArt(e.t, true)}
      </Box>
      <Chip size="small" label={est.label}
        sx={{ fontSize: 10, fontWeight: 800, height: 20, color: est.fg, bgcolor: est.bg, justifySelf: "start" }} />
      <Box>
        <Typography sx={{ fontSize: 13.5, fontWeight: clave ? 700 : 500 }}>{e.titulo}</Typography>
        {actor && (
          <Typography sx={{ fontSize: 11.5, fontFamily: "monospace", color: "text.secondary", wordBreak: "break-word" }}>
            {actor}
          </Typography>
        )}
        {e.path && (
          <Typography sx={{ fontSize: 11.5, fontFamily: "monospace", color: "text.secondary", wordBreak: "break-all" }}>
            {e.metodo} {e.path}
          </Typography>
        )}
        {e.raw && (
          <>
            <MuiLink component="button" underline="hover" onClick={() => setAbierto(!abierto)}
              sx={{ fontSize: 11, mt: 0.3 }}>
              {abierto ? "ocultar log" : "ver log"}
            </MuiLink>
            <Collapse in={abierto}>
              <Box sx={{
                mt: 0.5, p: 1, bgcolor: "#0F172A", color: "#E2E8F0", borderRadius: 0.5,
                fontFamily: "monospace", fontSize: 10.5, whiteSpace: "pre-wrap", wordBreak: "break-all",
              }}>{e.raw}</Box>
            </Collapse>
          </>
        )}
      </Box>
    </Box>
  );
}

function AuditoriaPanel({ cvu, ref_iso }: { cvu: string; ref_iso: string | null }) {
  const [dias,    setDias]    = useState(7);
  const [estado,  setEstado]  = useState<"idle" | "corriendo" | "listo" | "error">("idle");
  const [res,     setRes]     = useState<AuditoriaCuenta | null>(null);
  const [error,   setError]   = useState<string | null>(null);
  const [ticks,   setTicks]   = useState(0);

  async function correr() {
    setEstado("corriendo"); setError(null); setRes(null); setTicks(0);
    try {
      const inicio = await iniciarAuditoria(cvu, dias, ref_iso);
      // El backend avisa acá si el ambiente no tiene los log groups configurados.
      if (inicio.estado === "error") { setError(inicio.error ?? "la búsqueda falló"); setEstado("error"); return; }
      if (inicio.estado !== "corriendo") { setRes(inicio); setEstado("listo"); return; }
      const fin = await esperarAuditoria(cvu, inicio.query_id, { onTick: setTicks });
      if (fin.estado === "error") { setError(fin.error ?? "la búsqueda falló"); setEstado("error"); return; }
      setRes({ ...inicio, ...fin }); setEstado("listo");
    } catch (e: any) {
      setError(String(e?.message ?? e)); setEstado("error");
    }
  }

  const eventos = res?.eventos ?? [];
  return (
    <Box sx={{ mt: 2, pt: 2, borderTop: "1px dashed", borderColor: "divider" }}>
      <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
        <Typography sx={{ fontSize: 13, fontWeight: 700 }}>¿Quién tocó esta cuenta?</Typography>
        <ToggleButtonGroup size="small" exclusive value={dias}
          onChange={(_, v) => v && setDias(v)} disabled={estado === "corriendo"}>
          <ToggleButton value={1} sx={{ fontSize: 11, py: 0.2 }}>1 día</ToggleButton>
          <ToggleButton value={7} sx={{ fontSize: 11, py: 0.2 }}>7 días</ToggleButton>
          <ToggleButton value={30} sx={{ fontSize: 11, py: 0.2 }}>30 días</ToggleButton>
        </ToggleButtonGroup>
        <Button size="small" variant="outlined" onClick={correr} disabled={estado === "corriendo"}
          startIcon={estado === "corriendo" ? <CircularProgress size={13} /> : <HistoryIcon fontSize="small" />}>
          {estado === "corriendo" ? `Buscando en los logs… ${ticks * 2}s` : "Buscar en los logs"}
        </Button>
      </Stack>
      <Typography sx={{ fontSize: 11.5, color: "text.secondary", mt: 0.5 }}>
        Busca los llamados a la API, el webhook de Coelsa y el conector del banco
        {ref_iso ? <> en los {dias} días previos a la última modificación</> : <> en los últimos {dias} días</>}.
        {" "}Escanea varios GB de log: 1 día tarda segundos, 30 días puede tardar minutos.
      </Typography>

      {error && <Alert severity="error" sx={{ mt: 1.5 }}>{error}</Alert>}

      {estado === "listo" && eventos.length === 0 && (
        <Alert severity="info" sx={{ mt: 1.5 }}>
          No hay llamados sobre esta CVU en la ventana consultada. Probá ampliar el rango:
          los logs de la API se guardan 180 días.
        </Alert>
      )}

      {estado === "listo" && eventos.length > 0 && (
        <Box sx={{ mt: 1.5 }}>
          <Typography sx={{ fontSize: 11.5, color: "text.secondary", mb: 0.5 }}>
            {eventos.length} evento{eventos.length === 1 ? "" : "s"} · horarios en hora Argentina
            {res?.desde && <> · ventana {fmtArt(res.desde)} → {fmtArt(res.hasta)}</>}
            {res?.truncado && " · hay más eventos de los que se muestran"}
          </Typography>
          <Paper variant="outlined" sx={{ overflow: "hidden" }}>
            {eventos.map((e, i) => <EventoFila key={`${e.t}#${i}`} e={e} />)}
          </Paper>
          <Typography sx={{ fontSize: 11, color: "text.secondary", mt: 0.7 }}>
            Un mismo llamado puede quedar anotado por varios handlers; acá se muestra una vez.
            La API key nunca aparece: el log solo guarda a qué collector pertenece.
          </Typography>
        </Box>
      )}
    </Box>
  );
}

function ResultCard({ r }: { r: CuentaImputada }) {
  const [copied, setCopied] = useState(false);
  const ultIso = lastUpdateIso(r.historia);
  const resumen =
    `La CVU ${r.cvu} pertenece a ${r.collector ?? "?"}.` +
    (r.collector_nombre ? `\nCollector: ${r.collector} — ${r.collector_nombre}${r.collector_tipo ? ` (${r.collector_tipo})` : ""}` : "") +
    (r.related_tax_id ? `\nCUIT: ${fmtCuit(r.related_tax_id)}` : "") +
    `\nProcesador: ${r.pais}#${r.banco}${r.acquirer ? ` · acquirer ${r.acquirer}` : ""}${r.vendor_id != null ? ` · vendor ${r.vendor_id}` : ""}` +
    `\nCuenta: alias ${r.alias ?? "—"}${r.customer_id ? `, cliente ${r.customer_id}` : ""}, ${r.asignada ? "asignada" : "sin asignar"}` +
    (ultIso ? `\nÚltima modificación: ${fmtArt(ultIso, true)} (hora Argentina)` : "");

  async function copiar() {
    try { await navigator.clipboard.writeText(resumen); setCopied(true); setTimeout(() => setCopied(false), 2000); }
    catch { /* clipboard no disponible */ }
  }

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" sx={{ mb: 1 }}>
        <Typography sx={{ fontWeight: 800, fontSize: 17 }}>{r.collector ?? "sin collector"}</Typography>
        {r.collector_nombre && (
          <Typography sx={{ color: "text.secondary", fontSize: 14 }}>
            — {r.collector_nombre}{r.collector_tipo ? ` (${r.collector_tipo})` : ""}
          </Typography>
        )}
        <Box sx={{ flex: 1 }} />
        {r.collector_activo === false && (
          <Chip size="small" label="COLLECTOR INACTIVO" sx={{ fontWeight: 700, color: "#B91C1C", bgcolor: "#FEE2E2" }} />
        )}
        <Chip size="small" label={`${r.pais ?? "?"} · ${r.banco ?? "?"}`} sx={{ fontWeight: 700 }} />
        <Chip size="small" label={r.asignada ? "asignada" : "sin asignar"}
          sx={{ fontWeight: 700, color: r.asignada ? "#166534" : "#6B7280",
                bgcolor: r.asignada ? "#DCFCE7" : "#F1F5F9" }} />
        <Tooltip title={copied ? "¡Copiado!" : "Copiar resumen (para pegar en el chat)"}>
          <IconButton size="small" onClick={copiar}><ContentCopyIcon fontSize="small" /></IconButton>
        </Tooltip>
      </Stack>
      <KV data={{
        "CVU / CLABE":      <span style={{ fontFamily: "monospace" }}>{r.cvu}</span>,
        "Alias":            r.alias && <span style={{ fontFamily: "monospace" }}>{r.alias}</span>,
        "Cliente (customer)": r.customer_id,
        "Vendor":           r.vendor_id != null ? String(r.vendor_id) : null,
        "CUIT relacionado": fmtCuit(r.related_tax_id),
        "Email":            r.email,
        "Procesador":       [r.payments_processor ?? r.processor, r.acquirer ? `acquirer ${r.acquirer}` : null]
                              .filter(Boolean).join(" · ") || null,
        // El ISO crudo de CoreDB está en UTC: mostrarlo solo, en hora Argentina,
        // evita el malentendido de leerlo como si fuera hora local.
        "Última modificación": ultIso && (
          <Box>
            <span style={{ fontFamily: "monospace", fontWeight: 700 }}>{fmtArt(ultIso, true)}</span>
            <Typography component="span" sx={{ fontSize: 11.5, color: "text.secondary", ml: 1 }}>
              hora Argentina · en la base figura {ultIso} (UTC)
            </Typography>
            <Typography sx={{ fontSize: 11.5, color: "text.secondary" }}>
              Es la hora del último cambio sobre la cuenta, no la del último cobro.
            </Typography>
          </Box>
        ),
        "Historia":         !ultIso && r.historia && (
          <Box sx={{ fontFamily: "monospace", fontSize: 11.5, whiteSpace: "pre-wrap", color: "text.secondary" }}>
            {r.historia}
          </Box>
        ),
      }} />
      <AuditoriaPanel cvu={r.cvu} ref_iso={ultIso} />
    </Paper>
  );
}

export default function MonitoreoBuscador() {
  const [q,       setQ]       = useState("");
  const [result,  setResult]  = useState<BuscarCuentaResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState<string | null>(null);

  async function buscar() {
    const query = q.trim();
    if (!query) return;
    setLoading(true); setError(null); setResult(null);
    try { setResult(await buscarCuenta(query)); }
    catch (e: any) { setError(String(e?.message ?? e)); }
    finally { setLoading(false); }
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>Buscar alias / CVU</Typography>
      <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
        Ingresá una CVU (22 dígitos), CLABE (18) o un alias exacto y te dice a qué collector
        corresponde la cuenta imputada. Fuente: <code>payment_accounts</code> (core, solo lectura).
        Cada resultado trae además la auditoría de quién la tocó, desde los logs de la API.
      </Typography>

      <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
        <TextField size="small" label="CVU / CLABE / alias (ej. 0000277800000000562564 o final01)"
          value={q} onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && buscar()}
          sx={{ width: 440 }} inputProps={{ maxLength: 60, style: { fontFamily: "monospace" } }} />
        <Button variant="contained" onClick={buscar} disabled={loading || !q.trim()}
          startIcon={loading ? <CircularProgress size={14} color="inherit" /> : <SearchIcon />}>
          Buscar
        </Button>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {result && (result.items?.length ?? 0) === 0 && (
        <Alert severity="warning">
          No se encontró ninguna cuenta para <b>{result.query}</b>
          {result.tipo === "alias"
            ? " — el alias tiene que ser exacto (no hay búsqueda parcial)."
            : " en los bancos conocidos (BIND / CMFP / CMF / STP)."}
          {" "}Si es una cuenta externa (del pagador, no nuestra), acá no va a aparecer.
        </Alert>
      )}

      {result && (result.items?.length ?? 0) > 1 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {result.items.length} cuentas coinciden con <b>{result.query}</b>.
        </Alert>
      )}

      {result?.items?.map((r, i) => <ResultCard key={`${r.cvu}#${i}`} r={r} />)}
    </Box>
  );
}
